import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonNestedTable from '@/components/common/CommonNestedTable';
import { TableColumn } from '@/components/common/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import {
  filterOptions,
  GetFilterUrl,
  getStatusWarning,
  showToastNotification,
  statusOptions,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CategoryStates {
  isLoading: boolean;
  categoryStatus: string;
  categories: ICategoryData[];
  pageInfo: PageInfo;
  selectedCategories: Set<string | number>;
  showDeleteModel: boolean;
  selectedCategoryId: string | null;
  showUpdateModel: boolean;
  statusToUpdate: string | null;
  orderDir: string;
}
const Categories = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();

  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [states, setStates] = useState<CategoryStates>({
    isLoading: false,
    categoryStatus: '',
    categories: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedCategories: new Set(),
    showDeleteModel: false,
    selectedCategoryId: null,
    showUpdateModel: false,
    statusToUpdate: null,
    orderDir: 'newest',
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const onStatusUpdate = async (id: string, status: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${id}`;
      await callApi(PATH, { method: 'PATCH', data: { status } });
      fetchCategories();
    } catch (error) {
      // console.log('error', error);
      handleChangeState({ isLoading: false });
    }
  };

  const fetchCategories = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.categoryStatus,
        search: debounceValue,
        sort: states.orderDir,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}?${query ? `${query}` : ''}`;
      const res = await callApi<ICategoryResponse>(PATH, { method: 'GET' });
      // console.log('res is', res);
      handleChangeState({
        categories: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      // console.log('error is', error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      fetchCategories();
      showToastNotification('success', 'Category deleted successfully');
    } catch (error) {
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.categoryStatus,
    debounceValue,
    states.orderDir,
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.categories);

  const columns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Category',
      className: 'p-3',
      render: (item) => {
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${item?.media[0]?.url}`}
            text={item.name}
          />
        );
      },
    },
    {
      key: 'productCount',
      header: 'Products',
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          canEditVal={'category'}
          onChange={(status) => {
            handleChangeState({
              showUpdateModel: true,
              selectedCategoryId: item.id,
              statusToUpdate: status,
            });
          }}
        />
      ),
    },
  ];

  const childColumns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Sub Category',
      render: (item) => {
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${item?.media[0]?.url}`}
            text={item.name}
          />
        );
      },
    },
    {
      key: 'colorCode',
      header: 'Color Code',
      render: (i) => {
        return (
          <span
            style={{ backgroundColor: i.colorCode }}
            className="p-2 rounded-md"
          >
            {i.colorCode}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          canEditVal={'category'}
          onChange={(status) => {
            onStatusUpdate(item.id, status);
          }}
        />
      ),
    },
  ];

  const categoryActionConfig: Record<string, ActionConfig<ICategoryData>> = {
    edit: {
      text: 'Edit',
      onClick: (category) =>
        navigate(`${RoutesLink.addCategory}/${category.id}`),
      permission: () => canEdit('category'),
    },
    delete: {
      text: 'Delete',
      onClick: (category) => {
        handleChangeState({
          showDeleteModel: true,
          selectedCategoryId: category.id,
        });
      },
      permission: () => canDelete('category'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(categoryActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        limit,
      },
    });
  };

  const filterOption = [
    { value: 'az', label: 'A To Z' },
    { value: 'za', label: 'Z To A' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
  ];

  return (
    <>
      <Layout>
        <PageHeading text="Categories" />
        <Card>
          <div className="flex justify-between items-center gap-3 ">
            <CommonFilter
              filters={filterOptions}
              onChange={(filter) => {
                handleChangeState({
                  categoryStatus: filter,
                  pageInfo: { ...states.pageInfo, page: 1 },
                });
              }}
              filterOptions={{
                values: filterOption,
                onChange: (value) => handleChangeState({ orderDir: value }),
              }}
              onSearch={(text) => setSearchValue(text)}
              inputPlaceHolder="Search for Categories..."
            />
            <ExportOption
              APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/export/excel`}
              status={states.categoryStatus}
            />
          </div>
          <CommonNestedTable
            columns={columns}
            data={states.categories}
            showCheckbox={false}
            selectedItems={selectedItems}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
            getItemId={(item) => item.id}
            emptyMessage="No Categories Found"
            loading={states.isLoading}
            actions={actions}
            getChildren={(item) => item.subcategories}
            childColumns={childColumns}
          />
          <Pagination
            paginationData={states.pageInfo}
            onPageChange={(page) => handleChangePage(page)}
            isLoading={states.isLoading}
            onPageSizeChange={(size) => handleSizeChange(size)}
          />
        </Card>
        {states.showDeleteModel && (
          <ConfirmationModel
            type="category"
            show={states.showDeleteModel}
            onClose={() => handleChangeState({ showDeleteModel: false })}
            onConfirm={() =>
              handleDeleteCategory(states.selectedCategoryId as string)
            }
          />
        )}
        {states.showUpdateModel && (
          <ConfirmationModel
            modelType="update"
            show={states.showUpdateModel}
            warning={
              states.statusToUpdate === 'inactive'
                ? getStatusWarning('category')
                : ''
            }
            onClose={() => {
              handleChangeState({
                showUpdateModel: false,
                selectedCategoryId: null,
              });
            }}
            subTitle={`This action will update the status to ${states.statusToUpdate}`}
            onConfirm={() =>
              onStatusUpdate(
                states.selectedCategoryId as string,
                states.statusToUpdate as string,
              )
            }
          />
        )}
      </Layout>
    </>
  );
};

export default Categories;
